import { ArrowLeft, CheckCircle, Edit, Eye } from "lucide-react";
import type React from "react";
import type { TestCase, TestResult } from "../types";
import { openDocumentUrl } from "../utils/documentUrl";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Card } from "./ui/card";

interface PartnerResultsViewProps {
	result: TestResult;
	testCase: TestCase;
	onBack: () => void;
	onEdit: () => void;
}

export const PartnerResultsView: React.FC<PartnerResultsViewProps> = ({
	result,
	testCase,
	onBack,
	onEdit,
}) => {
	const isCertified = result.status === "certified";

	return (
		<div>
			<Button type="button" variant="ghost" onClick={onBack} className="mb-5">
				<ArrowLeft size={20} />
				Back to Test Cases
			</Button>

			<Card className="rounded-xl border p-8">
				{/* Header */}
				<div className="mb-6 flex items-start justify-between">
					<div>
						<h2 className="mb-3 text-(--color-primary) text-2xl">
							{testCase.name}
						</h2>
						<Badge
							variant={
								result.status === "certified"
									? "success"
									: result.status === "submitted"
										? "warning"
										: "secondary"
							}
							className="inline-flex items-center gap-1.5"
						>
							{result.status === "certified" && <CheckCircle size={16} />}
							{result.status === "certified"
								? "Certified by MoneyGram"
								: "Awaiting Certification"}
						</Badge>
					</div>

					{!isCertified && (
						<Button type="button" variant="outline" size="sm" onClick={onEdit}>
							<Edit size={16} />
							Edit Results
						</Button>
					)}
				</div>

				{/* Test Results */}
				<div className="mb-6 rounded-(--radius-sm) bg-(--color-muted) p-6">
					<h3 className="mt-0 mb-5 text-(--color-foreground) text-lg">
						Test Results
					</h3>

					<div className="grid grid-cols-2 gap-5">
						{result.reference_number && (
							<div>
								<div className="mb-1 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wider">
									MoneyGram Reference
								</div>
								<div className="font-medium font-mono text-(--color-foreground) text-base">
									{result.reference_number}
								</div>
							</div>
						)}

						{result.date_of_birth && (
							<div>
								<div className="mb-1 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wider">
									Date of Birth
								</div>
								<div className="font-medium text-(--color-foreground) text-base">
									{result.date_of_birth}
								</div>
							</div>
						)}

						{result.transaction_status && (
							<div>
								<div className="mb-1 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wider">
									Status
								</div>
								<div className="font-medium text-(--color-foreground) text-base">
									{result.transaction_status}
								</div>
							</div>
						)}

						{result.amount && (
							<div>
								<div className="mb-1 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wider">
									Amount
								</div>
								<div className="font-medium text-(--color-foreground) text-base">
									${result.amount} USD
								</div>
							</div>
						)}

						{result.fees && (
							<div>
								<div className="mb-1 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wider">
									Fees
								</div>
								<div className="font-medium text-(--color-foreground) text-base">
									${result.fees} USD
								</div>
							</div>
						)}

						{result.amount_received && (
							<div>
								<div className="mb-1 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wider">
									Amount Received
								</div>
								<div className="font-medium text-(--color-foreground) text-base">
									{result.amount_received}
								</div>
							</div>
						)}

						{result.transaction_id && (
							<div className="col-span-2">
								<div className="mb-1 font-semibold text-(--color-muted-foreground) text-xs uppercase tracking-wider">
									Transaction ID
								</div>
								<div className="break-all font-medium font-mono text-(--color-foreground) text-sm">
									{result.transaction_id}
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Screenshot */}
				{result.screenshot_url && (
					<div className="mb-6 rounded-(--radius-sm) bg-(--color-muted) p-6">
						<h3 className="mt-0 mb-4 text-(--color-foreground) text-lg">
							Screenshot
						</h3>
						<Button
							type="button"
							onClick={() => openDocumentUrl(result.screenshot_url ?? "")}
						>
							<Eye size={18} />
							View Screenshot
						</Button>
					</div>
				)}

				{/* Notes */}
				{result.notes && (
					<div className="mb-6 rounded-(--radius-sm) bg-(--color-muted) p-6">
						<h3 className="mt-0 mb-3 text-(--color-foreground) text-lg">
							Your Notes
						</h3>
						<p className="m-0 text-(--color-foreground) leading-relaxed">
							{result.notes}
						</p>
					</div>
				)}

				{/* Admin Certification Notes */}
				{isCertified && result.admin_notes && (
					<div className="mb-6 rounded-(--radius-sm) border border-(--color-primary) bg-(--color-info-50) p-6">
						<h3 className="mt-0 mb-3 text-(--color-primary) text-lg">
							MoneyGram Certification Notes
						</h3>
						<p className="m-0 text-(--color-foreground) leading-relaxed">
							{result.admin_notes}
						</p>
					</div>
				)}

				{/* Metadata */}
				<div className="rounded-(--radius-sm) bg-(--color-muted) px-5 py-4 text-(--color-muted-foreground) text-sm">
					<div className="mb-1.5">
						<strong>Submitted:</strong>{" "}
						{new Date(result.submitted_at).toLocaleString()}
					</div>
					{result.certified_at && (
						<div>
							<strong>Certified:</strong>{" "}
							{new Date(result.certified_at).toLocaleString()}
						</div>
					)}
				</div>
			</Card>
		</div>
	);
};
